import { useQuery } from '@tanstack/react-query';
import { objectStorageService } from '../../services/objectStorageService';
import type { StorageBucket, QuoteResponse } from '../../types/objectStorage';
import { formatUSD, formatINR } from '../../utils/formatCurrency';

interface Props {
  bucket: StorageBucket;
  projectId: string;
  variant?: 'card' | 'embedded';
}

export default function BucketPlanInfo({ bucket, projectId, variant = 'card' }: Props) {
  const planType = bucket.plan_type ?? 'legacy';
  const commitmentTB = bucket.min_commitment_tb ?? 0;

  // Fetch a projected quote for non-legacy plans.
  // Legacy buckets read from fixed plan fields only.
  const { data: quote } = useQuery({
    queryKey: ['bucket-quote', projectId, bucket.id, planType, commitmentTB, bucket.current_size_bytes],
    queryFn: () =>
      objectStorageService.getQuote(projectId, {
        bytes: bucket.current_size_bytes,
        plan_type: planType as 'starter' | 'committed',
        commitment_tb: commitmentTB || undefined,
      }),
    enabled: !!projectId && (planType === 'starter' || planType === 'committed'),
    staleTime: 1000 * 60,
  });

  const inner =
    planType === 'legacy' ? (
      <LegacyView bucket={bucket} />
    ) : (
      <NewPlanView
        planType={planType as 'starter' | 'committed'}
        commitmentTB={commitmentTB}
        bucket={bucket}
        quote={quote}
      />
    );

  if (variant === 'embedded') return <div className="space-y-1.5">{inner}</div>;
  return <div className="mt-1.5 space-y-1.5">{inner}</div>;
}

function LegacyView({ bucket }: { bucket: StorageBucket }) {
  return (
    <>
      <div className="flex items-center gap-2 text-xs">
        <span className="px-1.5 py-0.5 rounded-full bg-amber-500/10 text-amber-300 border border-amber-500/30">
          Legacy plan
        </span>
        {bucket.plan_id && (
          <span className="text-gray-300">{bucket.plan_id}</span>
        )}
        {bucket.plan_storage_gb > 0 && (
          <span className="text-gray-400">· {bucket.plan_storage_gb} GB quota</span>
        )}
      </div>
      <p className="text-[11px] text-gray-400">
        This bucket is on a legacy fixed-tier plan. New plans (Pay-as-you-go / Committed) have
        zero egress fees and no quota caps - see{' '}
        <a href="/drive/pricing" className="text-primary-300 hover:text-primary-200 underline">
          /drive/pricing
        </a>.
      </p>
      {bucket.plan_monthly_price_inr > 0 && (
        <p className="text-xs text-gray-300">
          Renewal:{' '}
          <span className="font-mono">{formatINR(bucket.plan_monthly_price_inr)}</span> per cycle.
        </p>
      )}
    </>
  );
}

function NewPlanView({
  planType,
  commitmentTB,
  bucket,
  quote,
}: {
  planType: 'starter' | 'committed';
  commitmentTB: number;
  bucket: StorageBucket;
  quote?: QuoteResponse;
}) {
  const isStarter = planType === 'starter';
  return (
    <>
      <div className="flex items-center gap-2 text-xs">
        <span
          className={`px-1.5 py-0.5 rounded-full ${
            isStarter
              ? 'bg-sky-500/10 text-sky-300 border border-sky-500/30'
              : 'bg-primary-500/10 text-primary-300 border border-primary-500/30'
          }`}
        >
          {isStarter ? 'Pay-as-you-go' : 'Committed'}
        </span>
        {!isStarter && commitmentTB > 0 && (
          <span className="text-gray-300">{commitmentTB} TB minimum</span>
        )}
      </div>
      <p className="text-[11px] text-gray-400">
        {isStarter
          ? '₹800/TB per month · billed monthly, no lock-in · zero egress'
          : '₹700/TB per month · billed for max(usage, commitment) · zero egress'}
      </p>
      {quote ? (
        <div className="rounded border border-gray-700 bg-gray-900/40 p-2 space-y-0.5 text-xs">
          <div className="flex justify-between">
            <span className="text-gray-400">Current month (projected)</span>
            <span className="font-mono text-gray-200">{formatUSD(quote.usd_cost)}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-400">USD → INR (today)</span>
            <span className="font-mono text-gray-300">₹{quote.inr_before_gst.toFixed(2)}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-400">GST 18%</span>
            <span className="font-mono text-gray-300">+ ₹{quote.gst_amount_inr.toFixed(2)}</span>
          </div>
          <div className="flex justify-between border-t border-gray-700 pt-1 mt-1">
            <span className="text-gray-200 font-medium">Total at renewal</span>
            <span className="font-mono text-primary-300 font-semibold">
              ₹{quote.inr_with_gst.toFixed(2)}
            </span>
          </div>
        </div>
      ) : (
        <p className="text-[11px] text-gray-500">Loading projection…</p>
      )}
    </>
  );
}
